import * as THREE from 'three';
import {
  ScreenSpaceMaterialConfig,
  SegmentationMaterialConfig,
  CutoutSegmentationMaterialConfig
} from './materialConfig.js';

export class MaterialManager {
  constructor(scene, videoInsert, videoCanvas, maskCanvas, width, height, postprocess, materialReplacements = {}, dynamicMaterials = []) {
    // Configuration de base
    this.postprocess = postprocess;
    this.videoInsert = videoInsert;
    this.videoCanvas = videoCanvas;
    this.maskCanvas = maskCanvas;
    this.scene = scene;
    this.width = width;
    this.height = height;

    // Configuration des remplacements de matériaux
    this.materialReplacements = materialReplacements;

    // Matériaux dynamiques à créer
    this.dynamicMaterials = dynamicMaterials;

    // Initialisation des textures
    this.canvasTexture = null;
    this.maskTexture = null;

    // Création des matériaux - une seule fois
    this.materials = this.createAllMaterials();    // Références rapides pour la rétrocompatibilité
    this.screenSpaceMat = this.materials.screenSpaceMask;
    this.segmentationMat = this.materials.segmentationMask;
    this.cutoutSegmentationMat = this.materials.cutoutMask;
  }
  // Création centralisée de tous les matériaux avec noms explicites
  createAllMaterials() {
    this.canvasTexture = new THREE.VideoTexture(this.videoCanvas);
    this.maskTexture = new THREE.VideoTexture(this.maskCanvas);
    this.maskTexture.premultiplyAlpha = true;

    const materials = {};

    // Matériau Screen Space - nommage explicite
    materials.screenSpaceMask = new THREE.ShaderMaterial({
      name: 'screenSpaceMask', // Nom explicite du matériau
      uniforms: {
        map: { value: this.canvasTexture },
        uResolution: { value: new THREE.Vector2(this.width, this.height) },
        useColorCorrection: { value: this.postprocess ? 1.0 : 0.0 }
      },
      vertexShader: ScreenSpaceMaterialConfig.vertexShader,
      fragmentShader: ScreenSpaceMaterialConfig.fragmentShader
    });

    materials.segmentationMask = new THREE.ShaderMaterial({
      name: 'segmentationMask', // Nom explicite du matériau
      uniforms: {
        map: { value: this.canvasTexture },
        uResolution: { value: new THREE.Vector2(this.width, this.height) },
        useColorCorrection: { value: this.postprocess ? 1.0 : 0.0 },
        alphaMap: { value: this.maskTexture },
        threshold: { value: 0.5 }
      },
      vertexShader: CutoutSegmentationMaterialConfig.vertexShader,
      fragmentShader: CutoutSegmentationMaterialConfig.fragmentShader,
      transparent: false,
      alphaTest: 0.5,
      depthWrite: true,
      depthTest: true
    });

    // Matériau Segmentation - nommage explicite
    // materials.segmentationMask1 = new THREE.ShaderMaterial({
    //   name: 'segmentationMask1', // Nom explicite du matériau
    //   uniforms: {
    //     map: { value: this.canvasTexture },
    //     uResolution: { value: new THREE.Vector2(this.width, this.height) },
    //     useColorCorrection: { value: this.postprocess ? 1.0 : 0.0 },
    //     alphaMap: { value: this.maskTexture },
    //     minThreshold: { value: 0.55 },
    //     maxThreshold: { value: 0.6 }
    //   },
    //   vertexShader: SegmentationMaterialConfig.vertexShader,
    //   fragmentShader: SegmentationMaterialConfig.fragmentShader,
    //   transparent: true
    // });

    // Matériau Cutout Segmentation - nommage explicite




    // Créer les matériaux dynamiques définis dans les settings
    this.createDynamicMaterials(materials);

    return materials;
  }

  // Création des matériaux dynamiques définis dans les settings de scène
  createDynamicMaterials(materials) {
    if (!this.dynamicMaterials || !Array.isArray(this.dynamicMaterials)) {
      return;
    }

    this.dynamicMaterials.forEach(materialConfig => {
      if (!materialConfig.name) {
        console.warn('⚠️ Matériau dynamique sans nom, ignoré:', materialConfig);
        return;
      }

      // Éviter d'écraser des matériaux existants
      if (materials[materialConfig.name]) {
        console.warn(`⚠️ Le matériau "${materialConfig.name}" existe déjà, il sera écrasé`);
      }

      try {
        const material = this.createMaterialFromConfig(materialConfig);
        if (material) {
          materials[materialConfig.name] = material;
          console.log(`✅ Matériau dynamique créé: "${materialConfig.name}"`);
        }
      } catch (error) {
        console.error(`❌ Erreur lors de la création du matériau "${materialConfig.name}":`, error);
      }
    });
  }

  // Création d'un matériau à partir de sa configuration
  createMaterialFromConfig(config) {
    switch (config.type) {
      case 'MeshPhysicalMaterial':
        return new THREE.MeshPhysicalMaterial({
          name: config.name,
          color: config.color || 0xffffff,
          roughness: config.roughness !== undefined ? config.roughness : 0.5,
          metalness: config.metalness !== undefined ? config.metalness : 0,
          iridescence: config.iridescence !== undefined ? config.iridescence : 0,
          iridescenceIOR: config.iridescenceIOR !== undefined ? config.iridescenceIOR : 1.3,
          iridescenceThicknessRange: config.iridescenceThicknessRange || [100, 400],
          reflectivity: config.reflectivity !== undefined ? config.reflectivity : 0.5,
          transmission: config.transmission !== undefined ? config.transmission : 0,
          side: config.side !== undefined ? config.side : THREE.FrontSide,
          transparent: config.transparent !== undefined ? config.transparent : false,
          opacity: config.opacity !== undefined ? config.opacity : 1
        });

      case 'MeshStandardMaterial':
        return new THREE.MeshStandardMaterial({
          name: config.name,
          color: config.color || 0xffffff,
          roughness: config.roughness !== undefined ? config.roughness : 0.5,
          metalness: config.metalness !== undefined ? config.metalness : 0,
          side: config.side !== undefined ? config.side : THREE.FrontSide,
          transparent: config.transparent !== undefined ? config.transparent : false,
          opacity: config.opacity !== undefined ? config.opacity : 1
        });

      case 'MeshBasicMaterial':
        return new THREE.MeshBasicMaterial({
          name: config.name,
          color: config.color || 0xffffff,
          side: config.side !== undefined ? config.side : THREE.FrontSide,
          transparent: config.transparent !== undefined ? config.transparent : false,
          opacity: config.opacity !== undefined ? config.opacity : 1
        });

      default:
        console.warn(`⚠️ Type de matériau non supporté: "${config.type}"`);
        return null;
    }
  }

  adjustCanvas(width, height) {
    // Mettre à jour la résolution pour tous les matériaux
    Object.values(this.materials).forEach(material => {
      if (material && material.uniforms && material.uniforms.uResolution) {
        material.uniforms.uResolution.value.set(width, height);
        material.needsUpdate = true;
      }
    });
  }

  detectedUpdate() {
    this.canvasTexture.needsUpdate = true;
    this.maskTexture.needsUpdate = true;
  }

  updatePostprocessMode(postprocessEnabled) {
    this.postprocess = postprocessEnabled;

    // Mettre à jour tous les matériaux avec le nouveau mode
    Object.values(this.materials).forEach(material => {
      if (material && material.uniforms && material.uniforms.useColorCorrection) {
        material.uniforms.useColorCorrection.value = postprocessEnabled ? 1.0 : 0.0;
        material.needsUpdate = true;
      }
    });
  }


  getMaterialByName(materialName) {
    const material = this.materials[materialName];
    if (!material) {
      console.warn(`⚠️ Matériau inconnu: "${materialName}". Matériaux disponibles: ${Object.keys(this.materials).join(', ')}`);
      return null;
    }
    return material;
  }
  // Méthode pour appliquer les remplacements de matériaux configurés (simplifié)
  applyMaterialReplacements(modelObject = null, modelConfig = null) {

    this.updateMaterialTextures();

    const replacements = this.buildReplacementMap(modelConfig);

    // Si un objet modèle spécifique est fourni, ne traiter que celui-ci
    const targetObject = modelObject || this.scene;

    targetObject.traverse((child) => {
      if (child.isMesh && child.material && child.material.name) {
        // //console.log(child.material)
        const originalMaterialName = child.material.name;
        const replacementMaterialName = replacements[originalMaterialName];
        if (child.material.type === "MeshStandardMaterial") {
          if (child.material.map) {
            // child.material.map.magFilter = THREE.NearestFilter;
            // child.material.map.minFilter = THREE.NearestFilter;
          }
        }
        if (replacementMaterialName) {
          const newMaterial = this.getMaterialByName(replacementMaterialName);
          if (newMaterial) {
            child.material = newMaterial;
            child.material.needsUpdate = true;
            //console.log(`🔄 Matériau "${originalMaterialName}" remplacé par "${replacementMaterialName}"`);
          }
        }
      }
    });
  }

  // Construire la map de remplacement en fusionnant global et spécifique au modèle
  buildReplacementMap(modelConfig) {
    let replacements = { ...this.materialReplacements };

    if (modelConfig && modelConfig.materialReplacements) {
      replacements = { ...replacements, ...modelConfig.materialReplacements };
    }

    return replacements;
  }

  // Méthode pour mettre à jour les textures dans tous les matériaux
  updateMaterialTextures() {
    //  //console.log('Updating material textures');
    Object.values(this.materials).forEach(material => {

      if (material && material.uniforms) {
        if (material.uniforms.map) {
          material.uniforms.map.value = this.canvasTexture;
        }
        if (material.uniforms.alphaMap) {
          material.uniforms.alphaMap.value = this.maskTexture;
        }
      }
    });
  }

  // Nettoyage des ressources
  dispose() {
    // console.log('🧹 Nettoyage du MaterialManager');

    // Disposer des textures
    if (this.canvasTexture) {
      this.canvasTexture.dispose();
      this.canvasTexture = null;
    }

    if (this.maskTexture) {
      this.maskTexture.dispose();
      this.maskTexture = null;
    }

    // Disposer de tous les matériaux
    Object.values(this.materials).forEach(material => {
      if (material) {
        // Disposer des textures dans les uniforms
        if (material.uniforms) {
          Object.values(material.uniforms).forEach(uniform => {
            if (uniform.value && uniform.value.dispose) {
              uniform.value.dispose();
            }
          });
        }
        material.dispose();
      }
    });

    this.materials = {};
    this.screenSpaceMat = null;
    this.segmentationMat = null;
    this.cutoutSegmentationMat = null;
  }
}